export type SectionType = 'regular' | 'announcements' | 'closed' | 'information';

export type SectionStatus = 'active' | 'hidden';

export type AccessSetting = 'all' | 'admins' | 'groups' | 'sectors' | 'role_levels';

/** Кто видит чат/форум в сайдбаре */
export type ViewAccessSetting = 'all' | 'invite';

export type ChannelKind = 'chat' | 'forum';

export type ChannelAccessGrant = {
  id: string;
  channelKind: ChannelKind;
  channelId: string;
  userId?: string;
  userEmail: string;
  userLabel: string;
  grantedAt: string;
};

export type TopicStatus = 'open' | 'closed' | 'pinned' | 'hidden';

export type AuthorProfile = {
  pseudonym: string;
  industry?: string;
  sector: string;
  roleLevel: string;
  company?: string;
  experienceRange?: string;
  isAnonymous?: boolean;
};

/** Группа в левой панели (бывшая подпись «Разделы») */
export type CommunitySidebarHeader = {
  id: string;
  title: string;
  sortOrder: number;
  status: SectionStatus;
  createdAt: string;
  updatedAt: string;
};

/** Форум: список тем и ответов */
export type CommunityForum = {
  id: string;
  headerId: string;
  name: string;
  description: string;
  icon: string;
  type: SectionType;
  sortOrder: number;
  status: SectionStatus;
  createTopicsAccess: AccessSetting;
  replyAccess: AccessSetting;
  viewAccess: ViewAccessSetting;
  topicCount?: number;
  createdAt: string;
  updatedAt: string;
};

/** @deprecated alias — используйте CommunityForum */
export type CommunitySection = CommunityForum;

/** Текстовый чат под заголовком */
export type CommunityChat = {
  id: string;
  headerId: string;
  name: string;
  description: string;
  icon: string;
  sortOrder: number;
  status: SectionStatus;
  postAccess: AccessSetting;
  viewAccess: ViewAccessSetting;
  messageCount?: number;
  lastActivityAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type ChatMessage = {
  id: string;
  chatId: string;
  body: string;
  author: AuthorProfile;
  /** Ключ автора (email) для проверки «своего» сообщения */
  authorKey?: string;
  createdAt: string;
  updatedAt?: string;
};

export type TopicSummary = {
  body: string;
  preparedBy: 'moderator' | 'datacircle';
  updatedAt: string;
};

export type CommunityTopic = {
  id: string;
  sectionId: string;
  title: string;
  description: string;
  tags: string[];
  author: AuthorProfile;
  status: TopicStatus;
  hasSummary: boolean;
  summary?: TopicSummary;
  replyCount: number;
  lastActivityAt: string;
  createdAt: string;
};

export type TopicReply = {
  id: string;
  topicId: string;
  body: string;
  author: AuthorProfile;
  authorKey?: string;
  createdAt: string;
  updatedAt?: string;
};

export type CommunityForumState = {
  headers: CommunitySidebarHeader[];
  forums: CommunityForum[];
  chats: CommunityChat[];
  chatMessages: ChatMessage[];
  topics: CommunityTopic[];
  replies: TopicReply[];
  accessGrants: ChannelAccessGrant[];
};

export type NavSelection =
  | { kind: 'forum'; id: string }
  | { kind: 'chat'; id: string };

export const SECTION_TYPE_LABELS: Record<SectionType, string> = {
  regular: 'Обычный форум',
  announcements: 'Форум объявлений',
  closed: 'Закрытый форум',
  information: 'Информационный форум',
};

export const ACCESS_SETTING_LABELS: Record<AccessSetting, string> = {
  all: 'Все участники',
  admins: 'Только администраторы',
  groups: 'По выбранным группам',
  sectors: 'По выбранным секторам',
  role_levels: 'По выбранным уровням роли',
};

export const VIEW_ACCESS_LABELS: Record<ViewAccessSetting, string> = {
  all: 'Доступно всем',
  invite: 'По приглашению',
};

export const PRESET_TAGS = [
  'Компенсация',
  'Краткосрочный бонус',
  'Долгосрочная мотивация',
  'Уровень роли',
  'Карьерный переход',
  'Переговоры',
  'Оффер',
  'Прямые инвестиции',
  'Венчурные фонды',
  'Инвестбанкинг',
  'Сделки M&A',
  'Семейные офисы',
  'Корпоративный венчур',
  'Аналитик',
  'Ассоциат',
  'Вице-президент',
  'Директор',
  'Партнер',
  'Основатель',
] as const;
